import { createRoute } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useState } from "react";

import { ProtectedRoute } from "../auth/ProtectedRoute";
import { SettingsTable } from "../components/cockpit/settings/SettingsTable";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useCollaboratorMutations, useCollaborators, type Collaborator } from "../queries";
import { rootRoute } from "./__root";

const collaboratorRoles: Collaborator["role"][] = [
  "owner",
  "general_manager",
  "sales_rep",
  "dispatcher",
  "technician",
  "read_only",
  "tech_admin",
];
const collaboratorStatuses: Collaborator["status"][] = ["active", "paused", "on_leave", "inactive"];

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/team",
  component: TeamScreen,
});

function TeamScreen() {
  return (
    <ProtectedRoute requiredRoles={["owner", "gm"]}>
      <TeamContent />
    </ProtectedRoute>
  );
}

function TeamContent() {
  const { data, isLoading } = useCollaborators();
  const mutations = useCollaboratorMutations();
  const [editing, setEditing] = useState<Partial<Collaborator> | null>(null);

  return (
    <section className="space-y-5">
      <SettingsHeader title="Team" subtitle="Manage collaborators and alert contact channels." />
      <SettingsTable
        title={isLoading ? "Team members loading" : "Team members"}
        rows={data?.items || []}
        addLabel="Add collaborator"
        columns={[
          { key: "name", label: "Name", render: (row) => <span className="font-medium text-slate-950">{row.name}</span> },
          { key: "email", label: "Email", render: (row) => row.email || "-" },
          { key: "telegram", label: "Telegram", render: (row) => row.telegram_chat_id || "-" },
          { key: "vonigo", label: "Vonigo", render: (row) => row.vonigo_user_code || "-" },
          { key: "role", label: "Role", render: (row) => <Badge variant="slate">{row.role}</Badge> },
          { key: "status", label: "Status", render: (row) => row.status },
        ]}
        onAdd={() => setEditing({ role: "sales_rep", status: "active" })}
        onEdit={setEditing}
        onDeactivate={(row) => mutations.remove.mutate(row.id)}
      />
      {editing ? (
        <TeamForm
          value={editing}
          saving={mutations.create.isPending || mutations.update.isPending}
          onClose={() => setEditing(null)}
          onSave={(payload) => {
            if (editing.id) {
              mutations.update.mutate({ id: editing.id, payload }, { onSuccess: () => setEditing(null) });
            } else {
              mutations.create.mutate(payload, { onSuccess: () => setEditing(null) });
            }
          }}
        />
      ) : null}
    </section>
  );
}

function TeamForm({
  value,
  saving,
  onClose,
  onSave,
}: {
  value: Partial<Collaborator>;
  saving: boolean;
  onClose: () => void;
  onSave: (value: Partial<Collaborator>) => void;
}) {
  const [form, setForm] = useState(value);
  return (
    <Drawer title={value.id ? "Edit collaborator" : "Add collaborator"} onClose={onClose}>
      <Field label="Name"><Input value={form.name || ""} onChange={(event) => setForm({ ...form, name: event.target.value })} /></Field>
      <Field label="Email"><Input value={form.email || ""} onChange={(event) => setForm({ ...form, email: event.target.value })} /></Field>
      <Field label="Telegram chat ID"><Input value={form.telegram_chat_id || ""} onChange={(event) => setForm({ ...form, telegram_chat_id: event.target.value })} /></Field>
      <Field label="Vonigo user code"><Input value={form.vonigo_user_code || ""} onChange={(event) => setForm({ ...form, vonigo_user_code: event.target.value })} /></Field>
      <Field label="Role">
        <select className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm" value={form.role || "sales_rep"} onChange={(event) => setForm({ ...form, role: event.target.value as Collaborator["role"] })}>
          {collaboratorRoles.map((role) => <option key={role} value={role}>{role}</option>)}
        </select>
      </Field>
      <Field label="Status">
        <select className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm" value={form.status || "active"} onChange={(event) => setForm({ ...form, status: event.target.value as Collaborator["status"] })}>
          {collaboratorStatuses.map((status) => <option key={status} value={status}>{status}</option>)}
        </select>
      </Field>
      <Button className="w-full" disabled={saving} onClick={() => onSave(form)}>Save collaborator</Button>
    </Drawer>
  );
}

function SettingsHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div>
      <Badge variant="slate">Settings</Badge>
      <h1 className="mt-3 text-2xl font-semibold text-slate-950">{title}</h1>
      <p className="mt-1 text-sm text-slate-600">{subtitle}</p>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <label className="block"><span className="text-xs font-medium text-slate-600">{label}</span><div className="mt-1">{children}</div></label>;
}

function Drawer({ title, children, onClose }: { title: string; children: ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-950/30">
      <div className="h-full w-full max-w-md space-y-4 overflow-y-auto bg-white p-5 shadow-xl">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-slate-950">{title}</h3>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </div>
        {children}
      </div>
    </div>
  );
}
